import { Storage } from "../storage/Storage";
import { LOGS, parseLogCategory } from "./categories";
import { getSinkPolicy, setSinkPolicy } from "./core";
import { withCategory } from "./policy";

const STORAGE_KEY = "mailvista.logs.console.categories";

type Listener = () => void;

const listeners = new Set<Listener>();
let snapshot: readonly LOGS[] = [];
let isRestored = false;

const sameCategories = (a: readonly LOGS[], b: readonly LOGS[]): boolean =>
    a.length === b.length &&
    a.every((category, index) => category === b[index]);

// Même tableau tant que le contenu ne change pas : contrat de `useSyncExternalStore`.
export const getConsoleCategories = (): readonly LOGS[] => {
    const current = getSinkPolicy("console")?.categories ?? [];

    if (!sameCategories(current, snapshot)) {
        snapshot = current;
    }

    return snapshot;
};

export const subscribeConsoleCategories = (
    listener: Listener,
): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const apply = (categories: readonly LOGS[]): void => {
    setSinkPolicy("console", { categories: [...categories] });
    listeners.forEach((listener) => listener());
};

export const setConsoleCategoryEnabled = (
    category: LOGS,
    enabled: boolean,
): void => {
    const next = withCategory(getConsoleCategories(), category, enabled);

    apply(next);
    Storage.setItem(STORAGE_KEY, JSON.stringify(next));
};

const parseStored = (raw: string | null): LOGS[] | null => {
    if (!raw) {
        return null;
    }

    try {
        const ids: unknown = JSON.parse(raw);

        if (!Array.isArray(ids)) {
            return null;
        }

        return ids
            .map((id) =>
                typeof id === "string" ? parseLogCategory(id) : undefined,
            )
            .filter((category): category is LOGS => category !== undefined);
    } catch {
        return null;
    }
};

export const restoreConsoleCategories = (): void => {
    if (isRestored) {
        return;
    }

    isRestored = true;

    const stored = parseStored(Storage.getItem(STORAGE_KEY));

    if (stored) {
        apply(stored);
    }
};
